import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  signIn, 
  signUp, 
  signOut, 
  confirmSignUp, 
  getCurrentUser, 
  fetchAuthSession, 
  fetchUserAttributes,
  confirmResetPassword,
  resetPassword
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import authConfig from '../config/auth.config';

// Log the current domain to help with debugging has been removed

const IS_LOCALHOST = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.includes('192.168.');

const COOKIE_DOMAIN = IS_LOCALHOST ? 'localhost' : '.screamify.com';

document.cookie = `testCookie=works; domain=${COOKIE_DOMAIN}; path=/; max-age=3600${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;

const initializeFromSharedCookies = async () => {
  const allCookies = document.cookie;
  const clientId = authConfig.Auth.userPoolWebClientId;
  const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;

  const hasCognitoIdToken = allCookies.includes('CognitoIdToken');
  const hasCognitoAccessToken = allCookies.includes('CognitoAccessToken');
  const hasLastAuthUser = allCookies.includes('LastAuthUser');
  const hasAmplifyLastAuthUser = allCookies.includes(`${amplifyPrefix}.LastAuthUser`);

  const cookiesArray = allCookies.split('; ');
  let lastAuthUserCookie = cookiesArray.find(cookie => cookie.startsWith(`${amplifyPrefix}.LastAuthUser`));
  let userId = null;

  if (lastAuthUserCookie) {
    userId = lastAuthUserCookie.split('=')[1];
  } else {
    lastAuthUserCookie = cookiesArray.find(cookie => cookie.startsWith('LastAuthUser='));
    if (lastAuthUserCookie) {
      userId = lastAuthUserCookie.split('=')[1];
    }
  }

  let hasAmplifyIdToken = false;
  let hasAmplifyAccessToken = false;

  if (userId) {
    const userPrefix = `${amplifyPrefix}.${userId}`;
    hasAmplifyIdToken = cookiesArray.some(cookie => cookie.startsWith(`${userPrefix}.idToken`));
    hasAmplifyAccessToken = cookiesArray.some(cookie => cookie.startsWith(`${userPrefix}.accessToken`));
  }

  const hasAllRequiredCustomCookies = hasCognitoIdToken && hasCognitoAccessToken && hasLastAuthUser;
  const hasAllRequiredAmplifyFormattedCookies = hasAmplifyIdToken && hasAmplifyAccessToken && hasAmplifyLastAuthUser;

  if (hasAllRequiredCustomCookies || hasAllRequiredAmplifyFormattedCookies) {
    try {
      let idToken = null;
      let accessToken = null;
      let refreshToken = null;

      if (hasAllRequiredAmplifyFormattedCookies && userId) {
        const userPrefix = `${amplifyPrefix}.${userId}`;
        for (const cookie of cookiesArray) {
          if (cookie.startsWith(`${userPrefix}.idToken=`)) {
            idToken = cookie.split('=')[1];
          } else if (cookie.startsWith(`${userPrefix}.accessToken=`)) {
            accessToken = cookie.split('=')[1];
          } else if (cookie.startsWith(`${userPrefix}.refreshToken=`)) {
            refreshToken = cookie.split('=')[1];
          }
        }
      } else {
        for (const cookie of cookiesArray) {
          if (cookie.startsWith('CognitoIdToken=')) {
            idToken = cookie.split('=')[1];
          } else if (cookie.startsWith('CognitoAccessToken=')) {
            accessToken = cookie.split('=')[1];
          } else if (cookie.startsWith('CognitoRefreshToken=')) {
            refreshToken = cookie.split('=')[1];
          }
        }
      }

      if (!idToken || !accessToken) {
        return false;
      }

      try {
        const amplifyConfig = {
          Auth: {
            Cognito: {
              userPoolId: authConfig.Auth.userPoolId,
              userPoolClientId: authConfig.Auth.userPoolWebClientId,
              loginWith: {
                email: true,
                phone: true,
                username: true
              }
            }
          },
          region: authConfig.Auth.region,
          cookieStorage: authConfig.Auth.cookieStorage,
          oauth: authConfig.Auth.oauth
        };

        Amplify.configure(amplifyConfig);

        const session = await fetchAuthSession({ forceRefresh: true });
        if (session?.tokens) {
          return true;
        }
      } catch (sessionError) {
        // Continue with next approach
      }

      try {
        const user = await getCurrentUser();
        if (user) {
          return true;
        }
      } catch (userError) {
        // Continue without throwing
      }
      
      return false;
    } catch (error) {
      return false;
    }
  } else {
    return false;
  }
};

initializeFromSharedCookies()
  .then(result => {
    // Initialization result handled silently
  })
  .catch(err => {
    // Error handled silently
  });

const checkForAuthCookies = () => {
  const allCookies = document.cookie;
  const cookiesArray = allCookies.split('; ');
  const clientId = authConfig.Auth.userPoolWebClientId;
  const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;

  const cognitoCookies = cookiesArray.filter(cookie => 
    cookie.startsWith('CognitoIdentityServiceProvider') || 
    cookie.includes(`${amplifyPrefix}.LastAuthUser`) ||
    cookie.includes('.idToken') ||
    cookie.includes('.accessToken') ||
    cookie.includes('.refreshToken') ||
    cookie.includes('LastAuthUser') ||
    cookie.includes('CognitoIdToken') ||
    cookie.includes('CognitoAccessToken') ||
    cookie.includes('CognitoRefreshToken') ||
    cookie.includes('ScreamifyAuth')
  );

  const hasCognitoCookies = cognitoCookies.length > 0;
  const hasAmplifyFormattedCookies = cookiesArray.some(cookie => cookie.startsWith(amplifyPrefix));

  let authDataInLocalStorage = false;
  try {
    const keys = Object.keys(localStorage);
    const authKeys = keys.filter(key => key.includes('CognitoIdentityServiceProvider'));
    authDataInLocalStorage = authKeys.length > 0;
  } catch (e) {
    // Error in localStorage check handled silently
  }
  
  return { 
    hasCognitoCookies, 
    hasAmplifyFormattedCookies,
    cognitoCookies, 
    authDataInLocalStorage 
  };
};

const { hasCognitoCookies, cognitoCookies, authDataInLocalStorage } = checkForAuthCookies();

const setAuthCookies = (tokens) => {
  if (!tokens) {
    return;
  }
  
  try {
    const maxAge = 60 * 60 * 24 * 365;
    const clientId = authConfig.Auth.userPoolWebClientId;
    const userId = tokens.idToken?.payload?.sub || tokens.accessToken?.payload?.username || 'unknown';
    const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
    const userPrefix = `${amplifyPrefix}.${userId}`;
    
    document.cookie = `${amplifyPrefix}.LastAuthUser=${userId}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    
    if (tokens.idToken) {
      const idToken = tokens.idToken.toString();
      document.cookie = `${userPrefix}.idToken=${idToken}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
      document.cookie = `CognitoIdToken=${idToken}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    }
    
    if (tokens.accessToken) {
      const accessToken = tokens.accessToken.toString();
      document.cookie = `${userPrefix}.accessToken=${accessToken}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
      document.cookie = `CognitoAccessToken=${accessToken}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    }
    
    if (tokens.refreshToken) {
      const refreshToken = tokens.refreshToken.toString();
      document.cookie = `${userPrefix}.refreshToken=${refreshToken}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
      document.cookie = `CognitoRefreshToken=${refreshToken}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    }
    
    try {
      if (tokens.idToken?.payload) {
        const payload = tokens.idToken.payload;
        const expiryDate = payload.exp ? new Date(payload.exp * 1000).toISOString() : '';
        document.cookie = `${amplifyPrefix}.userData=${encodeURIComponent(JSON.stringify({
          username: payload['cognito:username'] || payload.sub, 
          email: payload.email,
          exp: expiryDate
        }))}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
      }
    } catch (e) {
      // Error setting userData cookie handled silently
    }
    
    const timestamp = new Date().toISOString();
    document.cookie = `ScreamifyAuth=${timestamp}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    document.cookie = `ScreamifyAuthBackup=${timestamp}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
    
    if (IS_LOCALHOST) {
      try {
        localStorage.setItem('screamify_auth_timestamp', timestamp);
        localStorage.setItem('screamify_auth_user_id', userId);
      } catch (e) {
        // Error setting localStorage handled silently
      }
    }
    
    setTimeout(() => {
      const { hasCognitoCookies, cognitoCookies } = checkForAuthCookies();
    }, 100);
  } catch (error) {
    // Error setting auth cookies handled silently
  }
};

const AuthContext = createContext(null);

const redirectPaths = {
  dashboard: '/',
  login: '/login'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const manualInitForCrossDomain = async () => {
      const initialized = await initializeFromSharedCookies();
      
      if (initialized) {
        return checkAuthState();
      } 
      
      try {
        const allCookies = document.cookie;
        const cookiesArray = allCookies.split('; ');
        let idToken = null;
        let accessToken = null;
        let username = null;
        
        for (const cookie of cookiesArray) {
          if (cookie.startsWith('CognitoIdToken=')) {
            idToken = cookie.split('=')[1];
          } else if (cookie.startsWith('CognitoAccessToken=')) {
            accessToken = cookie.split('=')[1];
          } else if (cookie.startsWith('LastAuthUser=')) {
            username = cookie.split('=')[1];
          }
        }
        
        if (idToken && accessToken && username) {
          const directInitResult = await initializeWithTokens(idToken, accessToken);
          if (directInitResult) {
            return checkAuthState();
          }
        }
      } catch (e) {
        // Manual token extraction error handled silently
      }
      
      return checkAuthState();
    };
    
    manualInitForCrossDomain().catch(error => {
      checkAuthState();
    });
  }, []);

  const isCookiesAvailable = () => {
    return navigator.cookieEnabled;
  };

  const checkAuthState = useCallback(async () => {
    setIsLoading(true);
    try {
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      console.log("checkAuthState: User found", currentUser.username, attributes);
      setUser({
        userId: currentUser.userId,
        username: currentUser.username,
        attributes: attributes,
        signInDetails: currentUser.signInDetails
      });
      setIsAuthenticated(true);
    } catch (err) {
      console.log("checkAuthState: No user found", err);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getToken = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      const token = session.tokens?.accessToken?.toString() || session.tokens?.idToken?.toString();
      return token || null;
    } catch (err) {
      console.log("getToken error:", err);
      return null;
    }
  };

  const handleSignIn = async (email, password) => {
    setIsLoading(true);
    setError(null);
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password,
        options: {
          authFlowType: "USER_PASSWORD_AUTH"
        }
      });
      console.log("handleSignIn result:", { isSignedIn, nextStep });

      if (isSignedIn) {
        return { isSignedIn: true };
      } else {
        return { isSignedIn, nextStep };
      }
    } catch (err) {
      console.error("Sign in error:", err);
      setError(err);
      if (err.name === 'UserNotFoundException') {
        throw new Error('User not found. Please check your email or sign up.');
      } else if (err.name === 'NotAuthorizedException') {
        throw new Error('Incorrect email or password.');
      } else if (err.name === 'UserNotConfirmedException') {
        throw new Error('Account not verified. Please check your email for a verification code.');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (email, password, displayName, uuid = null) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log(`Signing up user with email: ${email}, displayName: ${displayName}, uuid: ${uuid}`);
      
      // Fetch user's IP address
      const ip = await fetch('https://api.ipify.org?format=json')
        .then(r => r.json())
        .then(r => r.ip)
        .catch(() => 'unknown');
      
      console.log(`Fetched IP address: ${ip}`);
      
      // Build clientMetadata
      const clientMetadata = {
        ipAddress: ip
      };
      
      // Add UUID to clientMetadata if present
      if (uuid) {
        clientMetadata.uuid = uuid;
      }
      
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            'custom:displayName': displayName || email.split('@')[0]
          },
          clientMetadata: clientMetadata
        }
      });
      console.log('Cognito signUp response:', result);

      return {
        isSignUpComplete: result.isSignUpComplete,
        userId: result.userId,
        nextStep: result.nextStep
      };
    } catch (err) {
      console.error('Cognito signUp error:', err);
      setError(err);
      if (err.name === 'UsernameExistsException') {
        throw new Error("An account with this email already exists. Please sign in.");
      } else if (err.name === 'InvalidPasswordException') {
        throw new Error(err.message || 'Password does not meet requirements.');
      } else if (err.name === 'InvalidParameterException' && err.message.includes('displayName')) {
        throw new Error('Invalid display name.');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignUp = async (email, code) => {
    setIsLoading(true);
    setError(null);
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code
      });
      console.log("Confirmation successful for:", email);
      return { success: true };
    } catch (err) {
      console.error("Confirmation error:", err);
      setError(err);
      if (err.name === 'CodeMismatchException') {
        throw new Error('Invalid verification code. Please try again.');
      } else if (err.name === 'ExpiredCodeException') {
        throw new Error('Verification code has expired. Please request a new one.');
      } else if (err.name === 'UserNotFoundException') {
        throw new Error('User not found. Please try signing up again.');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signOut({ global: true });
      console.log("Sign out successful");
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      console.error("Sign out error:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getUserAttributes = () => {
    return user?.attributes || null;
  };
  
  const getDisplayName = () => {
    const attributes = getUserAttributes();
    return attributes ? attributes['custom:displayName'] : null;
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signOut: handleSignOut,
    confirmSignUp: handleConfirmSignUp,
    checkAuthState,
    getToken,
    getUserAttributes,
    getDisplayName,
    forgotPassword: async (username) => {
      if (!username) {
        console.error("Password reset error: username/email is required");
        throw new Error("Email is required to reset password");
      }
      
      console.log(`Initiating password reset for username: ${username}`);
      setIsLoading(true);
      
      try {
        const result = await resetPassword({ username });
        console.log(`Password reset initiated successfully for: ${username}`);
        return result;
      } catch (err) {
        console.error("Password reset error:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthService = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthService must be used within an AuthProvider');
  }
  return context;
};

const initializeWithTokens = async (idToken, accessToken) => {
  if (!idToken || !accessToken) {
    return false;
  }
  
  try {
    const amplifyConfig = {
      Auth: {
        Cognito: {
          userPoolId: authConfig.Auth.userPoolId,
          userPoolClientId: authConfig.Auth.userPoolWebClientId,
          loginWith: {
            email: true,
            phone: false,
            username: false
          }
        }
      },
      region: authConfig.Auth.region,
      cookieStorage: authConfig.Auth.cookieStorage,
      oauth: authConfig.Auth.oauth
    };
    
    Amplify.configure(amplifyConfig);
    
    const session = await fetchAuthSession({
      forceRefresh: true,
      bypassCache: true
    });
    
    if (session?.tokens) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

const validateIdToken = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }
    
    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);
    
    if (payload.exp && payload.exp < now) {
      return false;
    }
    
    if (!payload.sub || !payload.iss || !payload.aud) {
      return false;
    }
    
    if (payload.token_use !== 'id') {
      return false;
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

const authService = {
  async signIn(username, password) {
    try {
      const { isSignedIn } = await signIn({ username, password, options: { authFlowType: "USER_PASSWORD_AUTH" } });
      return isSignedIn;
    } catch (error) {
      console.error("Standalone signIn error", error);
      throw error;
    }
  },

  async signUp(username, password, email, displayName, uuid = null) {
    try {
      // Fetch user's IP address
      const ip = await fetch('https://api.ipify.org?format=json')
        .then(r => r.json())
        .then(r => r.ip)
        .catch(() => 'unknown');
      
      console.log(`Standalone signUp - Fetched IP address: ${ip}`);
      
      // Build clientMetadata
      const clientMetadata = {
        ipAddress: ip
      };
      
      // Add UUID to clientMetadata if present
      if (uuid) {
        clientMetadata.uuid = uuid;
      }
      
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username,
        password,
        options: { 
          userAttributes: { 
            email, 
            'custom:displayName': displayName 
          },
          clientMetadata: clientMetadata
        }
      });
      return { isSignUpComplete, userId, nextStep };
    } catch (error) {
      console.error("Standalone signUp error", error);
      throw error;
    }
  },

  async confirmSignUp(username, code) {
    try {
      await confirmSignUp({ username, confirmationCode: code });
      return true;
    } catch (error) {
      console.error("Standalone confirmSignUp error", error);
      throw error;
    }
  },

  async signOut() {
    try {
      await signOut({ global: true });
      return true;
    } catch (error) {
      console.error("Standalone signOut error", error);
      throw error;
    }
  },

  async getCurrentUser() {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      return { ...user, attributes };
    } catch (error) {
      return null;
    }
  },

  async getToken() {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || session.tokens?.idToken?.toString() || null;
    } catch (error) {
      return null;
    }
  },

  async forgotPassword(username) {
    try {
      await resetPassword({ username });
      return true;
    } catch (error) {
      console.error("Standalone forgotPassword error", error);
      throw error;
    }
  },

  async confirmForgotPassword(username, code, newPassword) {
    try {
      await confirmResetPassword({ username, confirmationCode: code, newPassword });
      return true;
    } catch (error) {
      console.error("Standalone confirmForgotPassword error", error);
      throw error;
    }
  },

  async isAuthenticated() {
    try {
      await getCurrentUser();
      return true;
    } catch {
      return false;
    }
  },
};

export default authService;